import queryKeys from '@/constants/queryKeys';
import { removeAdminContent } from '@/services/api/requests/content';
import { TCustomAxiosError } from '@/services/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useToast from '../useToast';

const useRemoveContent = (id: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { isPending, isSuccess, mutate } = useMutation({
    mutationFn: async (removalReason: string) => {
      const response = await removeAdminContent(id, removalReason);
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.byId, id] });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.all] });
      toast({ status: 'success', title: 'Success', description: 'Content removed.' });
      onSuccess?.();
    },
    onError: async (error: TCustomAxiosError) => {
      // Another admin may have already changed this item — refresh so the view reflects
      // the current status (and shows the correct action button).
      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.byId, id] });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.all] });
      toast({
        status: 'error',
        title: 'Error removing content',
        description: error.errors?.[0]?.message,
      });
    },
  });

  return { isPending, isSuccess, mutate };
};

export default useRemoveContent;
